// Pinned messages — pin toggle per message + collapsible pinned list.
//
// TODO (API gap): No pin endpoint exists in the generated admin-bff client
// (/api/v1/chats/{sessionId}/messages/{msgId}/pin). Pins use local state per
// session only until the BFF exposes a typed endpoint and orval regenerates.
// When the endpoint is available:
//   1. Replace usePinnedMessages local state with TanStack Query useQuery +
//      useMutation (no Zustand for server state).
//   2. Use adminBffFetch (or the generated client) to POST/DELETE pins.
//   3. Remove the pinned Set from this file; query key: chatKeys.pins(sessionId).

import { Badge, Button } from '@avila-roffe/components-web';
import { Pin, PinOff, X } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ChatMessage } from './chatApi';

// ─── Context ──────────────────────────────────────────────────────────────────
// Shared across a session so the pin toggle in MessageBubble and the panel
// in SessionView can communicate without prop-drilling.

interface PinnedMessagesContextValue {
  pinnedIds: Set<string>;
  toggle: (messageId: string) => void;
  isPinned: (messageId: string) => boolean;
}

const PinnedMessagesContext = createContext<PinnedMessagesContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

export interface PinnedMessagesProviderProps {
  children: React.ReactNode;
}

export function PinnedMessagesProvider({ children }: PinnedMessagesProviderProps) {
  const [pinnedIds, setPinnedIds] = useState<Set<string>>(new Set());

  const toggle = useCallback((messageId: string) => {
    setPinnedIds((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  }, []);

  const isPinned = useCallback((messageId: string) => pinnedIds.has(messageId), [pinnedIds]);

  const value = useMemo(() => ({ pinnedIds, toggle, isPinned }), [pinnedIds, toggle, isPinned]);

  return <PinnedMessagesContext.Provider value={value}>{children}</PinnedMessagesContext.Provider>;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function usePinnedMessages(): PinnedMessagesContextValue | null {
  return useContext(PinnedMessagesContext);
}

// ─── PinToggle (used in MessageBubble action bar) ─────────────────────────────

export interface PinToggleProps {
  messageId: string;
}

export function PinToggle({ messageId }: PinToggleProps) {
  const ctx = usePinnedMessages();
  if (!ctx) return null;
  const { toggle, isPinned } = ctx;
  const pinned = isPinned(messageId);

  return (
    <Button
      type="button"
      appearance="ghost"
      variant="secondary"
      size="xs"
      onClick={() => toggle(messageId)}
      aria-label={pinned ? 'Unpin message' : 'Pin message'}
      aria-pressed={pinned}
      className={pinned ? 'text-warning' : 'text-muted-foreground'}
      data-testid="pin-toggle"
    >
      {pinned ? <PinOff className="size-3" /> : <Pin className="size-3" />}
    </Button>
  );
}

// ─── PinnedMessagesPanel (used in session sidebar) ────────────────────────────

export interface PinnedMessagesPanelProps {
  /** All messages in the session — used to resolve pinned content by id. */
  messages: ChatMessage[];
  /** Called with the message id when the user clicks a pinned item. */
  onJumpTo?: (messageId: string) => void;
}

export function PinnedMessagesPanel({ messages, onJumpTo }: PinnedMessagesPanelProps) {
  const ctx = usePinnedMessages();
  const [open, setOpen] = useState(true);

  const pinned = useMemo(
    () => (ctx ? messages.filter((m) => ctx.pinnedIds.has(m.id)) : []),
    [ctx, messages],
  );

  if (!ctx || ctx.pinnedIds.size === 0) return null;
  const { pinnedIds, toggle } = ctx;

  return (
    <div className="border-b border-border" data-testid="pinned-messages-panel">
      {/* Header */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground"
        aria-expanded={open}
        aria-controls="pinned-list"
        data-testid="pinned-panel-toggle"
      >
        <Pin className="size-3 shrink-0" />
        <span>Pinned</span>
        <Badge variant="secondary" appearance="soft" size="sm" className="ml-1">
          {pinnedIds.size}
        </Badge>
        <span className="ml-auto text-[10px]">{open ? '▲' : '▼'}</span>
      </button>

      {/* List */}
      {open && (
        <ul
          id="pinned-list"
          className="flex flex-col gap-1 px-3 pb-2"
          aria-label="Pinned messages"
          data-testid="pinned-list"
        >
          {pinned.map((msg) => (
            <li key={msg.id} className="flex items-start gap-1.5 group/pinned-item">
              <button
                type="button"
                onClick={() => onJumpTo?.(msg.id)}
                className="flex-1 truncate text-left text-xs text-foreground hover:text-primary py-0.5"
                aria-label={`Jump to pinned message: ${msg.content.slice(0, 80)}`}
                data-testid={`pinned-item-${msg.id}`}
              >
                <span className="truncate">{msg.content}</span>
              </button>
              <button
                type="button"
                onClick={() => toggle(msg.id)}
                className="shrink-0 opacity-0 group-hover/pinned-item:opacity-100 text-muted-foreground hover:text-destructive"
                aria-label="Unpin"
                data-testid={`pinned-unpin-${msg.id}`}
              >
                <X className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
